import { IMapyBoxItem, IMapyboxTrackLinkEvent, TActivityType, TNoteVersion } from "~/interfaces";
import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { folderCreate, likeUpdate, trackCopy, trackLink, trackListFolders } from "~/util/providers/mapybox";
import { getMapyboxError, getUserTime, mapyboxNotification } from "~/util/mapybox";
import { MAX_TITLE_LEN } from "./const";
import ActivitySelect from "~/ui/activities/select";
import MyMapsFolderSelectCreate from "../mymaps/folder-select-create";
import { checkNoteVersion } from "~/util/user-note";
import DateSelect from "~/ui/dateselect";
import { store } from "~/state/store";
import { MAPYBOX_TRACK_LINK_EVENT_NAME } from "../mymaps/const";

interface IState {
	usertitle: string;
	note: string;
	trackType: TActivityType;
	/* eslint-disable camelcase */
	user_time: number;
	/* eslint-enable camelcase */
}

// ostatni maji pouze vyber slozky
type TType = "full" | "link-to-folder" | "link-to-another-folder";

interface IMyMapsLinkActivity extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	trackData: IMapyBoxItem;
	title?: string;
	type?: TType;
	isPremiumUser?: boolean;
	noteVersion?: TNoteVersion;
	onCreateFolder?: () => void;
	onSave?: (likeData: Partial<IMapyBoxItem>) => void;
	onError?: () => void;
}

const DEFAULT_TRACK_TYPE = "pesky";

export default class MyMapsLinkActivity {
	_opts: IMyMapsLinkActivity;

	_isReplace: boolean;

	_dialog: MyMapsDialog;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_noteElem: HTMLTextAreaElement;

	_folderSelectCreate: MyMapsFolderSelectCreate;

	_activitySelect: ActivitySelect;

	_hasUserTime: boolean;

	constructor(data: IMyMapsLinkActivity) {
		this._opts = {
			title: "",
			onSave: () => {},
			onCancel: () => {},
			onClose: () => {},
			onError: () => {},
			onCreateFolder: () => {},
			isPremiumUser: false,
			noteVersion: "version1",
			type: "full",
			...data,
		};
		this._state = {
			usertitle: "",
			note: "",
			trackType: DEFAULT_TRACK_TYPE,
			/* eslint-disable camelcase */
			user_time: Date.now(),
			...this._opts.trackData
				? {
					usertitle: this._opts.trackData.usertitle || this._opts.trackData.title || this._opts.title,
					note: this._opts.trackData.description || "",
					trackType: this._opts.trackData.data?.type || DEFAULT_TRACK_TYPE,
					/* eslint-disable camelcase */
					user_time: getUserTime(this._opts.trackData),
				}
				: {},
		};
		this._origState = {
			...this._state,
		};
		this._changed = false;
		this._noteElem = null;
		this._hasUserTime = Mapy.CUS.mapybox?.date;

		// nacteme linky
		this._loadTrackList();
		this._build();
	}

	open() {
		const { user } = store.getState().user;
		const open = () => {
			this._dialog?.open();
			!this._isReplace && this._noteElem?.focus();
		};

		if (user.state === "green") {
			open();
		} else {
			Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "save",
				},
				motivation: "save",
			}).then(() => {
				this._build();
				open();
			}, () => {});
		}
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._opts.onClose();
		this._dialog.close(force);
	}

	_build() {
		const title = this._opts.type === "link-to-another-folder"
			? _("mymap.contextMenu.link-to-another-folder")
			: _("mymap.contextMenu.link-to-folder");
		const dialog = new MyMapsDialog({
			titles: {
				main: [title],
				saveBtn: _("connect"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._opts.onCancel();
				this._closeDialog(true);
			},
		});
		const mainContent = dialog.contents[0];

		mainContent.classList.add("mymaps-dialog__defContent");

		// vybiratko typu
		if (Mapy.CUS.mapybox?.pathTypes && this._opts.type === "full") {
			this._activitySelect = new ActivitySelect({
				selected: this._state.trackType,
				onChange: trackType => {
					// pri editaci by nemelo byt mozne zadat all, ale kdyby se to nahodou stalo, tak to nezaneseme do dat a na server
					if (trackType === "all") {
						return;
					}

					this._updateState({ trackType });
				},
			});
			mainContent.append(this._activitySelect.container);
		}

		// titulek
		if (this._opts.type === "full") {
			const titleInput = createMyMapsInput({
				bold: true,
				placeholder: _("mymap.addPopup.name"),
				onChange: usertitle => {
					this._updateState({ usertitle });
				},
				text: this._state.usertitle,
				count: MAX_TITLE_LEN,
			});

			mainContent.append(titleInput.container);
		}

		// vyber slozky
		this._folderSelectCreate = new MyMapsFolderSelectCreate({
			onChange: () => {
				this._updateState({});
			},
		});

		mainContent.append(this._folderSelectCreate.container);

		// datum
		if (this._opts.type === "full" && this._hasUserTime) {
			const dateSelect = new DateSelect({
				date: new Date(this._state.user_time),
			});

			dateSelect.node.addEventListener("datechange", (event: CustomEvent) => {
				// eslint-disable-next-line camelcase
				this._updateState({ user_time: event.detail.date.getTime() });
			});
			mainContent.append(dateSelect.node);
		}

		// poznamka
		if (this._opts.type === "full" && (this._opts.isPremiumUser || checkNoteVersion(this._opts.noteVersion, "version2"))) {
			const noteData = createMyMapsNote({
				text: this._state.note,
				isPremiumUser: this._opts.isPremiumUser,
				noteVersion: this._opts.noteVersion,
				onChange: note => {
					this._updateState({ note });
				},
			});

			mainContent.append(noteData.container);
			this._noteElem = noteData.textarea;
		}

		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._dialog.setSaveBtnTitle = this._folderSelectCreate.state.createNewFolder ? _("mymap.folders.createAndSave") : _("connect");

		this._changed = this._folderSelectCreate.changed
			|| this._state.note !== this._origState.note
			|| this._state.usertitle !== this._origState.usertitle
			/* eslint-disable camelcase */
			|| this._state.user_time !== this._origState.user_time
			|| this._state.trackType !== this._origState.trackType;
	}

	_checkClose() {
		return !this._changed;
	}

	_sendError(errorMsg: string) {
		this._dialog.error = errorMsg;
		this._dialog.loading = false;
		this._opts.onError();
	}

	async _loadTrackList() {
		const trackFolders = await trackListFolders(this._opts.trackData.linktype === "track" ? this._opts.trackData.linkid : this._opts.trackData.id);

		this._folderSelectCreate.moveFolders = trackFolders.map(item => ({
			id: item.id,
			title: item.title,
		}));
	}

	async _onSave() {
		this._dialog.error = "";
		this._dialog.loading = true;

		const folderState = this._folderSelectCreate.state;
		let folderId = folderState.folderId;
		let newLikeData: Partial<IMapyBoxItem> = null;

		// nova slozka
		if (folderState.createNewFolder) {
			if (folderState.newFolderTitle === "") {
				this._dialog.loading = false;
				this._dialog.error = _("mymap.folders.emptyFolder");

				return;
			}

			try {
				const createData = await folderCreate(folderState.newFolderTitle);

				folderId = createData.id;
				// ulozime - pro pripad override
				this._folderSelectCreate.overwriteOrigState(folderId);
			} catch (exc) {
				this._sendError(getMapyboxError({
					operation: "folder-create",
				}));

				return;
			}
		}

		const trackId = this._opts.trackData.linkid || this._opts.trackData.id;

		// kopie tracku
		if (this._opts.trackData.foreign) {
			try {
				const trackCopyData = await trackCopy(trackId, folderId, this._state.usertitle, this._state.note);
				const newLikeId = trackCopyData.copied.filter(item => "new_id" in item)[0].new_id;

				await likeUpdate(newLikeId, {
					// @ts-ignore
					"data.info.trackType": this._state.trackType,
					...this._hasUserTime
						? {
							/* eslint-disable camelcase */
							user_time: this._state.user_time,
						}
						: {},
				});
				newLikeData = {
					id: newLikeId,
					usertitle: this._state.usertitle,
					description: this._state.note,
				};

				mapyboxNotification({
					operation: "track-copy",
					folderTitle: folderState.folderTitle,
					folderId,
				});
			} catch (exc) {
				this._sendError(getMapyboxError({
					operation: "track-copy",
				}));

				return;
			}
		} else {
			// link tracku
			try {
				newLikeData = await trackLink(trackId, folderId, {
					"usertitle": this._state.usertitle,
					"description": this._state.note,
					"data.info.trackType": this._state.trackType,
					// @ts-ignore
					...this._hasUserTime
						? {
							/* eslint-disable camelcase */
							user_time: this._state.user_time,
						}
						: {},
				});

				mapyboxNotification({
					operation: "track-link",
					folderTitle: folderState.folderTitle,
					folderId,
				});
			} catch (exc) {
				this._sendError(getMapyboxError({
					operation: "track-link",
				}));

				return;
			}
		}

		Mapy.getComponent("logger").log("my_maps_activity_to_folder", {
			id: newLikeData.id,
			type: this._opts.trackData.data?.info?.trackType,
		});
		window.dispatchEvent(new CustomEvent(MAPYBOX_TRACK_LINK_EVENT_NAME, {
			detail: {
				source: "track-link-dialog",
				id: trackId,
			} as IMapyboxTrackLinkEvent,
		}));

		this._opts.onSave({
			id: newLikeData.id,
			usertitle: this._state.usertitle,
			description: this._state.note,
		});
		this._closeDialog(true);
	}
}
